// ========== React & Libraries ==========
import { z } from 'zod';
import { useTimeZone } from '@/auth/useTimeZone.js';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// ========== Commons ==========
import {
  coachPaymentKind,
  coachPaymentKindToLabel,
  coachPaymentStatusKind,
  coachPaymentStatusToLabel,
  coachPaymentStatusToColor,
  CoachPaymentReport,
  coachPaymentReportSchema,
  CoachPaymentNoteToLabel,
  coachPaymentDepositInfoStatusSchema,
  CoachPaymentDepositInfoStatusToLabel,
  coachPaymentNoteKind,
} from '@airreading/common/data';

// ========== Utils & Helpers ==========
import {
  bulkMarkTeacherPaymentAsSent,
  bulkSendTeacherPayment,
  getOngoingPayments,
  getPaymentsCsv,
  getTotalSumFromLifetime,
} from '@/lib/api-calls.js';
import { validateRouteRole } from '@/lib/routing.js';

import { paginationRequestSchema } from '@/lib/pagination.js';

// ========== Table Components ==========
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';

// ========== Layout Components ==========
import { InfoPage } from '@/components/info-page.js';

// ========== Users/Teachers Components ==========
import { coachesSectionTabs } from '../-components/tabs.js';
import { isAirAdmin, isNotNullish, plainDateFromStringOrElseUndefined } from '@airreading/common';

import { airColumnHelper } from '@/lib/tables.js';
import { formatCurrency } from '@/lib/common-utils.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterPlainDatePicker } from '@/components/table/table-filter-plain-date-picker.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { usePayCycleOptions } from '@/lib/api-call-hooks.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { CreatePayment } from '../-components/payments/create-payment.js';
import { KebabOptions } from '../-components/payments/kebab.js';
import { CoachPaymentKindTag } from '../-components/payments/coach-payment-kind-tag.js';
import { FromOptions } from '../-components/payments/from-options.js';
import { Badge } from '@/components/ui/badge.js';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { BuildToastResponse } from './-build-toast-response.js';
import { BuildMarkAsSentToastResponse } from './-build-mark-as-sent-toast-response.js';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu.js';
import { PaymentInstrumentModal } from '../-components/payments/payment-instrument-modal.js';
import { TransferDetailsModal } from '../-components/payments/transfer-details-modal.js';
import { DepositInfoStatus } from '../-components/payments/deposit-info-status.js';

const payCycleRequestSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  type: z.array(coachPaymentKind).optional(),
  payDate: z.string().optional(),
  payCycleInterval: z.string().optional(),
  paymentSentStatus: z.array(coachPaymentStatusKind).optional(),
  depositInfoStatus: z.array(coachPaymentDepositInfoStatusSchema).optional(),
  notes: z.array(coachPaymentNoteKind).optional(),
});

const filterableCoachPaymentStatusKind = coachPaymentStatusKind.extract([
  'not_started',
  'processing',
  'processed',
  'failed',
]);

const columnHelper = airColumnHelper<CoachPaymentReport>(coachPaymentReportSchema.keyof());

const coachPaymentReportTableColumns = [
  createTableRowSelectionCell(columnHelper, {
    selectAllTestId: 'teacher-pay-table-checkbox-select-all',
    rowSelectTestId: (rowNumber) => `teacher-pay-table-checkbox-select-${rowNumber}`,
  }),

  columnHelper.accessor('coach', {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ getValue, row }) => {
      const coach = getValue();
      return (
        <Link
          to="/teachers/$teacherId"
          params={{ teacherId: coach.id }}
          data-testid={`teacher-pay-table-link-name-${row.index}`}
        >
          {coach.fullNameOrEmail}
        </Link>
      );
    },
  }),

  columnHelper.accessor('totalAmountInCents', {
    id: 'totalAmount',
    header: 'Total amount',
    cell: ({ getValue }) => {
      const totalAmountCent = getValue();
      return formatCurrency(totalAmountCent / 100);
    },
  }),

  columnHelper.accessor('payPeriodInterval', {
    id: 'payPeriod',
    header: 'Pay cycle',
    cell: ({ getValue }) => getValue(),
  }),

  columnHelper.accessor('payDate', {
    id: 'paymentDate',
    header: 'Pay date',
    cell: ({ getValue }) => getValue(),
  }),

  columnHelper.accessor('paymentStatus', {
    id: 'paymentStatus',
    header: 'Pay sent',
    cell: ({ getValue, row }) => {
      const status = getValue();

      return (
        <Badge
          variant="secondary"
          style={{
            color: coachPaymentStatusToColor[status ?? coachPaymentStatusKind.enum.not_started],
          }}
          className="bg-transparent  text-sm hover:bg-transparent focus:bg-transparent focus-visible:bg-transparent relative pl-0"
          data-testid={`teacher-pay-table-badge-payment-status-${row.index}`}
        >
          <span
            className="w-[10px] h-[10px] rounded-full mr-1"
            style={{ backgroundColor: coachPaymentStatusToColor[status ?? coachPaymentStatusKind.enum.not_started] }}
          />
          {coachPaymentStatusToLabel[status] ?? coachPaymentStatusToLabel[coachPaymentStatusKind.enum.not_started]}
        </Badge>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('type', {
    id: 'paymentType',
    header: 'Payment type',
    cell: ({ getValue, row }) => {
      return <CoachPaymentKindTag kind={getValue()} data-testid={`teacher-pay-table-badge-type-${row.index}`} />;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'totalSessions',
    header: 'Total sessions taught',
    cell: ({ getValue }) => {
      const report = getValue();

      if (report.type !== coachPaymentKind.enum.teaching) return;

      return report.totalSessionsTaught;
    },
  }),

  columnHelper.accessor('depositInfoStatus', {
    id: 'depositInfoStatus',
    header: 'Deposit info status',
    cell: ({ getValue }) => {
      const depositInfoStatus = getValue();

      return <DepositInfoStatus depositInfoStatus={depositInfoStatus} />;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'paymentInstrumentId',
    header: 'Brex - Payment Instrument ID',
    cell: ({ getValue }) => {
      const { coach, depositInfoStatus } = getValue();

      const { paymentInstrumentId } = coach;

      return <PaymentInstrumentModal paymentInstrumentId={paymentInstrumentId} depositInfoStatus={depositInfoStatus} />;
    },
  }),

  columnHelper.accessor('paySentId', {
    id: 'paySentId',
    header: 'Brex - Pay Sent ID',
    cell: ({ getValue }) => {
      const paySendId = getValue();

      return <TransferDetailsModal paySentId={paySendId} />;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'notes',
    header: 'Notes',
    cell: ({ getValue }) => {
      const report = getValue();
      return report.notes.map((note) => CoachPaymentNoteToLabel[note]).join(', ');
    },
  }),

  columnHelper.accessor('coach.email', {
    id: 'email',
    header: 'Email',
    cell: ({ getValue }) => getValue(),
  }),

  columnHelper.accessor((row) => row, {
    id: 'kebabOptions',
    header: '',
    cell: ({ getValue, row }) => {
      const report = getValue();
      return (
        <KebabOptions
          {...report}
          from={FromOptions.teacherPay}
          teacherId={report.coach.id}
          data-testid={`teacher-pay-table-button-actions-${row.index}`}
        />
      );
    },
  }),
];

export const Route = createFileRoute('/_layout/teachers/teacher-pay/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: TeacherPay,
  validateSearch: payCycleRequestSchema,
});

function TeacherPay() {
  const { timeZone } = useTimeZone();
  const [isSelectAllPagesActive, setIsSelectAllPagesActive] = useState(false);

  const queryClient = useQueryClient();

  const {
    pageSize,
    pageIndex,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      type: [[paymentKind, activePaymentKind], setPaymentKind],
      payDate: [[payDate, activePayDate], setPayDate],
      payCycleInterval: [[payCycleInterval, activePayCycleInterval], setPayCycleInterval],
      paymentSentStatus: [[paymentSentStatus, activePaymentSentStatus], setPaymentSentStatus],
      depositInfoStatus: [[depositInfoStatus, activeDepositInfoStatus], setDepositInfoStatus],
      notes: [[notes, activeNotes], setNotes],
    },
    resetFilters,
    activeFiltersCount,
  } = usePaginatedFilters(Route.id, [
    'query',
    'type',
    'payDate',
    'payCycleInterval',
    'paymentSentStatus',
    'depositInfoStatus',
    'notes',
  ]);

  const { toast } = useToast();

  const { data, isPending, refetch, error } = useQuery(
    getOngoingPayments({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        type: activePaymentKind,
        payDate: plainDateFromStringOrElseUndefined(activePayDate)?.toString(),
        payCycleInterval: activePayCycleInterval,
        paymentSentStatus: activePaymentSentStatus,
        depositInfoStatus: activeDepositInfoStatus,
        notes: activeNotes,
      },
    }),
  );

  const { data: totalSum } = useQuery(
    getTotalSumFromLifetime({
      filters: {
        query: activeNameQuery,
        type: activePaymentKind,
        payDate: plainDateFromStringOrElseUndefined(activePayDate)?.toString(),
        payCycleInterval: activePayCycleInterval,
        paymentSentStatus: activePaymentSentStatus,
        depositInfoStatus: activeDepositInfoStatus,
        notes: activeNotes,
      },
    }),
  );

  const { mutateAsync: bulkSendMutate } = useMutation({
    mutationFn: bulkSendTeacherPayment,
  });

  const { mutateAsync: bulkMarkAsSentMutate } = useMutation({
    mutationFn: bulkMarkTeacherPaymentAsSent,
  });

  const { rowSelection, getRowId, onRowSelectionChange, rowDataSelection, resetAllRowSelection } =
    useRowsBulkSelection<CoachPaymentReport>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => data?.payments.find((session) => session.id === id),
    });

  // Handle isSelectAllPagesActive changes and reset selections when deselecting all
  const handleSelectAllPagesChange = useCallback(
    (isActive: boolean) => {
      setIsSelectAllPagesActive(isActive);
      if (!isActive) {
        // When deselecting all pages, reset all row selections
        resetAllRowSelection();
      }
    },
    [resetAllRowSelection],
  );

  useEffect(() => {
    if (!isSelectAllPagesActive || !data?.payments || data.payments.length === 0) {
      return;
    }
    const allRowIds = data.payments.reduce(
      (acc, payment) => {
        acc[payment.id] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
    onRowSelectionChange(allRowIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSelectAllPagesActive]);

  // Reset isSelectAllPagesActive when filters change
  useEffect(() => {
    if (!isSelectAllPagesActive) {
      return;
    }
    setIsSelectAllPagesActive(false);
    resetAllRowSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameQuery, paymentKind, payDate, payCycleInterval]);

  const onSendPay = useCallback(
    async (data: CoachPaymentReport[]) => {
      if (!isSelectAllPagesActive) {
        const response = await bulkSendMutate({
          coachPaymentIds: data.map((payment) => payment.id),
        });

        toast({
          title: 'Pay sent',
          description: <BuildToastResponse response={response} />,
        });
      }
      if (isSelectAllPagesActive) {
        const response = await bulkSendMutate({
          query: activeNameQuery,
          type: activePaymentKind,
          payDate: plainDateFromStringOrElseUndefined(activePayDate)?.toString(),
          payCycleInterval: activePayCycleInterval,
        });

        toast({
          title: 'Pay sent',
          description: <BuildToastResponse response={response} />,
        });
      }

      queryClient.invalidateQueries(getOngoingPayments());
    },
    [
      isSelectAllPagesActive,
      toast,
      queryClient,
      bulkSendMutate,
      activeNameQuery,
      activePaymentKind,
      activePayDate,
      activePayCycleInterval,
    ],
  );

  const onMarkAsSent = useCallback(
    async (data: CoachPaymentReport[]) => {
      const baseFilters = {
        query: activeNameQuery,
        type: activePaymentKind,
        payDate: plainDateFromStringOrElseUndefined(activePayDate)?.toString(),
        payCycleInterval: activePayCycleInterval,
        paymentSentStatus: activePaymentSentStatus,
        depositInfoStatus: activeDepositInfoStatus,
        notes: activeNotes,
      };

      const response = await bulkMarkAsSentMutate(
        isSelectAllPagesActive ? baseFilters : { ...baseFilters, coachPaymentIds: data.map((payment) => payment.id) },
      );

      toast({
        title: 'Marked as Sent',
        description: <BuildMarkAsSentToastResponse response={response} />,
      });

      resetAllRowSelection();
      queryClient.invalidateQueries(getOngoingPayments());
    },
    [
      isSelectAllPagesActive,
      toast,
      queryClient,
      bulkMarkAsSentMutate,
      activeNameQuery,
      activePaymentKind,
      activePayDate,
      activePayCycleInterval,
      activePaymentSentStatus,
      activeDepositInfoStatus,
      activeNotes,
      resetAllRowSelection,
    ],
  );

  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  const onDownloadTransactions = useCallback(async () => {
    if (!activePayCycleInterval) {
      return;
    }

    const baseFilters = {
      query: activeNameQuery,
      type: activePaymentKind,
      payDate: plainDateFromStringOrElseUndefined(activePayDate)?.toString(),
      payCycleInterval: activePayCycleInterval,
      paymentSentStatus: activePaymentSentStatus,
      depositInfoStatus: activeDepositInfoStatus,
      notes: activeNotes,
    };

    try {
      setIsDownloadingCsv(true);
      await getPaymentsCsv(timeZone, {
        filters: isSelectAllPagesActive
          ? baseFilters
          : { ...baseFilters, coachPaymentIds: rowDataSelection.map((payment) => payment.id) },
      });
    } catch (e) {
      toast({
        title: 'Failed to download transactions',
        description: e instanceof Error ? e.message : 'Please try again.',
      });
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [
    activePayCycleInterval,
    activeNameQuery,
    activePaymentKind,
    activePayDate,
    activePaymentSentStatus,
    activeDepositInfoStatus,
    activeNotes,
    isSelectAllPagesActive,
    rowDataSelection,
    toast,
    timeZone,
  ]);

  const bulkActionsMenuItems = useMemo(
    () => (
      <>
        <DropdownMenuItem
          data-testid="teacher-pay-bulk-menu-item-send-pay"
          onSelect={(e) => {
            e.preventDefault();
            onSendPay(rowDataSelection);
          }}
        >
          Send pay
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="teacher-pay-bulk-menu-item-mark-as-sent"
          onSelect={(e) => {
            e.preventDefault();
            onMarkAsSent(rowDataSelection);
          }}
        >
          Mark as Sent
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="teacher-pay-bulk-menu-item-download-transactions"
          disabled={!activePayCycleInterval || isDownloadingCsv}
          onSelect={(e) => {
            e.preventDefault();
            onDownloadTransactions();
          }}
        >
          {isDownloadingCsv ? 'Downloading…' : 'Download transactions'}
        </DropdownMenuItem>
      </>
    ),
    [onSendPay, onMarkAsSent, rowDataSelection, activePayCycleInterval, isDownloadingCsv, onDownloadTransactions],
  );

  const payCycleOptions = usePayCycleOptions();

  const table = useReactTable({
    data: data?.payments ?? [],
    columns: coachPaymentReportTableColumns,
    getRowId,
    rowCount: data?.total ?? 0,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange,
    onPaginationChange,
    manualPagination: true,
    state: {
      rowSelection,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
  });

  const changeFilter = (filter: 'payDate' | 'payCycleInterval', value: unknown) => {
    if (filter === 'payDate') {
      setPayDate(value as string);
      setPayCycleInterval(undefined);
    }
    if (filter === 'payCycleInterval') {
      setPayCycleInterval(value as string);
      setPayDate(undefined);
    }
    if ((activePayDate && filter === 'payCycleInterval') || (activePayCycleInterval && filter === 'payDate')) {
      toast({
        title: 'Pay date and pay cycle interval must be selected',
        description: 'Please select a pay date and a pay cycle interval',
      });
    }
  };

  const filterComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search name or email"
          value={nameQuery ?? ''}
          onChange={(e) => setNameQuery(e.target.value)}
          data-testid="teacher-pay-filter-input-search"
        />
        <TableFilterDropdown
          title="Payment type"
          options={Object.values(coachPaymentKind.enum).map((kind) => ({
            label: coachPaymentKindToLabel[kind],
            value: kind,
          }))}
          selectedValues={paymentKind ?? []}
          onChange={setPaymentKind}
          data-testid="teacher-pay-filter-dropdown-payment-type"
        />
        <TableFilterPlainDatePicker
          selectedPlainDateString={payDate}
          onPlainDateSelect={(date) => {
            changeFilter('payDate', date?.toString());
          }}
          tableFilterDatePickerProps={{
            placeholder: 'Pay date',
          }}
          buttonTestId="teacher-pay-filter-button-pay-date"
        />
        <TableFilterDropdown
          title="Pay cycle"
          singleSelect
          options={payCycleOptions}
          selectedValues={[payCycleInterval].filter(isNotNullish)}
          onChange={(selected) => {
            changeFilter('payCycleInterval', selected.filter((v) => v !== payCycleInterval)[0]);
          }}
          data-testid="teacher-pay-filter-dropdown-pay-cycle"
        />
        <TableMoreFiltersMenu
          activeFiltersCount={activeFiltersCount}
          triggerTestId="teacher-pay-filter-button-more-filters"
          filterItems={[
            {
              title: 'Pay sent status',
              filterComponent: (
                <TableFilterDropdown
                  title="Pay sent status"
                  options={filterableCoachPaymentStatusKind.options.map((status) => ({
                    label: coachPaymentStatusToLabel[status],
                    value: status,
                  }))}
                  selectedValues={paymentSentStatus ?? []}
                  onChange={setPaymentSentStatus}
                  data-testid="teacher-pay-filter-dropdown-pay-sent-status"
                />
              ),
            },
            {
              title: 'Deposit info status',
              filterComponent: (
                <TableFilterDropdown
                  title="Deposit info status"
                  options={coachPaymentDepositInfoStatusSchema.options.map((status) => ({
                    label: CoachPaymentDepositInfoStatusToLabel[status],
                    value: status,
                  }))}
                  selectedValues={depositInfoStatus ?? []}
                  onChange={setDepositInfoStatus}
                  data-testid="teacher-pay-filter-dropdown-deposit-info-status"
                />
              ),
            },
            {
              title: 'Payment notes',
              filterComponent: (
                <TableFilterDropdown
                  title="Payment notes"
                  options={coachPaymentNoteKind.options.map((status) => ({
                    label: CoachPaymentNoteToLabel[status],
                    value: status,
                  }))}
                  selectedValues={notes ?? []}
                  onChange={setNotes}
                  data-testid="teacher-pay-filter-dropdown-payment-notes"
                />
              ),
            },
          ]}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} data-testid="teacher-pay-filter-button-reset" />
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      nameQuery,
      paymentKind,
      setPaymentKind,
      payDate,
      setPayDate,
      payCycleOptions,
      payCycleInterval,
      setPayCycleInterval,
      resetFilters,
      setNameQuery,
    ],
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        data-testid="teacher-pay-table"
        rowTestId={(_, rowIndex) => `teacher-pay-table-row-${rowIndex}`}
        cellTestId={(_, columnId, rowIndex) => `teacher-pay-table-cell-${columnId}-${rowIndex}`}
        endMessageTestId="teacher-pay-table-end-message"
        filters={{ filterLeftComponents: filterComponents }}
        autoManagedColumnIds={['selection']}
        topBarRightComponents={<CreatePayment from={FromOptions.teacherPay} data-testid="teacher-pay-button-add" />}
        bulkActionsMenuItems={bulkActionsMenuItems}
        bulkActionsTestId="teacher-pay-button-bulk-actions"
        isLoading={isPending}
        hasError={!!error}
        emptyState="No payments found"
        errorState={<TableErrorState refetchAction={refetch} />}
        isSelectAllPagesActive={isSelectAllPagesActive}
        onSelectAllPagesChange={handleSelectAllPagesChange}
        totalRowCount={data?.total}
        topBarBottomComponents={
          <p className="text-sm font-semibold" data-testid="teacher-pay-sum">
            SUM{' '}
            <span className="font-bold text-2xl" data-testid="teacher-pay-sum-amount">
              {formatCurrency((totalSum ?? 0) / 100)}
            </span>
          </p>
        }
      />
    </InfoPage>
  );
}
